/**
 * Placement and superweapon targeting modes (U14, R11, R31).
 *
 * Clicking a `ReadyToPlace` icon enters placement: the cursor carries a
 * footprint ghost with per-cell legal/illegal tinting, left-click commits, and
 * Esc or right-click **suspends** back to `ReadyToPlace` without cancelling.
 *
 * Suspend rather than cancel is the only correct exit, because the production
 * state machine deliberately has no `ReadyToPlace → Cancelled` edge: a finished
 * structure awaiting placement is Red Alert's behaviour and worth preserving.
 */

import { Vector3 } from 'three'
import type { Ctx, Subsystem } from '../core/registry.js'
import { Renderer } from '../render/index.js'
import { Build } from '../sim/build.js'
import { World } from '../sim/world.js'
import { CMD, REASON_TEXT, type Command, type Reason } from '../net/commands.js'
import type { Relay } from '../net/relay.js'
import { defById } from '../sim/data/entities.js'
import { MAP_SIZE } from '../core/config.js'

export const MODE = {
  None: 0,
  Placement: 1,
  SuperTarget: 2,
} as const

export type Mode = (typeof MODE)[keyof typeof MODE]

export class PlacementMode implements Subsystem {
  static id = 'placement'
  static deps = ['render', 'build', 'world'] as const

  private ctx!: Ctx
  private renderer!: Renderer
  private build!: Build
  private world!: World

  relay: Relay | null = null
  localSlot = 0

  private mode: Mode = MODE.None
  private kind = 0
  /** Anchor cell under the cursor, or -1. */
  private cx = -1
  private cy = -1
  private failure: Reason | null = null

  private banner!: HTMLDivElement

  constructor(private readonly root: HTMLElement) {}

  init(ctx: Ctx): void {
    this.ctx = ctx
    this.renderer = ctx.get<Renderer>('render')
    this.build = ctx.get<Build>('build')
    this.world = ctx.get<World>('world')

    this.banner = document.createElement('div')
    this.banner.className = 'ba-placement'
    this.banner.style.display = 'none'
    this.root.append(this.banner)
  }

  get active(): boolean {
    return this.mode !== MODE.None
  }

  get current(): Mode {
    return this.mode
  }

  /** Enter placement for a structure that is awaiting a footprint. */
  enterPlacement(kind: number): boolean {
    if (!this.build.isReadyToPlace(this.localSlot, kind)) return false
    this.mode = MODE.Placement
    this.kind = kind
    this.refreshBanner()
    return true
  }

  /** Enter superweapon targeting. Refused while the weapon is still charging. */
  enterSuperTarget(): boolean {
    if (!this.build.isSuperweaponReady(this.localSlot)) return false
    this.mode = MODE.SuperTarget
    this.kind = this.build.superKind[this.localSlot]
    this.refreshBanner()
    return true
  }

  /**
   * Leave the mode without committing. For placement this is a **suspend**:
   * the structure stays in `ReadyToPlace` and the icon still pulses.
   */
  cancel(): void {
    this.mode = MODE.None
    this.kind = 0
    this.failure = null
    this.cx = -1
    this.cy = -1
    this.banner.style.display = 'none'
    this.renderer.setPlacementGhost(null)
  }

  /** Track the cursor. Returns true if the current cell is legal. */
  onPointerMove(clientX: number, clientY: number): boolean {
    if (this.mode === MODE.None) return false
    const world = this.renderer.screenToWorld(clientX, clientY, this.scratch)
    if (!world) {
      this.renderer.setPlacementGhost(null)
      return false
    }

    const def = defById(this.kind)
    if (this.mode === MODE.SuperTarget) {
      this.cx = Math.floor(world.x)
      this.cy = Math.floor(world.z)
      this.failure = this.world.inBounds(this.cx, this.cy) ? null : 'out-of-bounds'
      this.renderer.setPlacementGhost({ cx: this.cx, cy: this.cy, w: 1, h: 1, legal: !this.failure })
      this.refreshBanner()
      return this.failure === null
    }

    if (!def) return false
    // The footprint is centred on the cursor, as the player expects.
    this.cx = Math.floor(world.x) - (def.width >> 1)
    this.cy = Math.floor(world.z) - (def.height >> 1)
    this.failure = this.build.footprintFailure(this.localSlot, def, this.cx, this.cy, true)
    this.renderer.setPlacementGhost({
      cx: this.cx,
      cy: this.cy,
      w: def.width,
      h: def.height,
      legal: this.failure === null,
    })
    this.refreshBanner()
    return this.failure === null
  }

  /** Commit at the current cell. Returns true if a command was submitted. */
  commit(): boolean {
    if (this.mode === MODE.None || !this.relay) return false
    if (this.cx < 0 || this.cy < 0) return false

    if (this.failure) {
      // Named, not silent, and the mode stays open so the player can try again.
      this.ctx.emit('order:rejected', { owner: this.localSlot, reason: this.failure })
      return false
    }

    const cmd =
      this.mode === MODE.Placement
        ? { t: CMD.Place, kind: this.kind, cx: this.cx, cy: this.cy }
        : { t: CMD.SuperFire, kind: this.kind, cx: this.cx, cy: this.cy }
    this.relay.submit(cmd as Omit<Command, 'slot' | 'seq'>)
    this.cancel()
    return true
  }

  update(): void {
    if (this.mode === MODE.Placement && !this.build.isReadyToPlace(this.localSlot, this.kind)) {
      // The structure was placed, or its producer died. Leave the mode rather
      // than holding a ghost for something that no longer exists.
      this.cancel()
    }
  }

  private refreshBanner(): void {
    if (this.mode === MODE.None) {
      this.banner.style.display = 'none'
      return
    }
    const name = defById(this.kind)?.name ?? 'Structure'
    this.banner.style.display = 'block'
    this.banner.classList.toggle('illegal', this.failure !== null)
    this.banner.textContent =
      this.mode === MODE.SuperTarget
        ? `${name}: click a target — Esc to cancel`
        : this.failure
          ? `${name}: ${REASON_TEXT[this.failure] ?? this.failure}`
          : `${name}: click to place — Esc to hold`
  }

  /** Cell under the cursor, for tests. */
  get anchor(): { cx: number; cy: number; failure: Reason | null } {
    return { cx: this.cx, cy: this.cy, failure: this.failure }
  }

  dispose(): void {
    this.banner.remove()
  }

  private readonly scratch = new Vector3()
}

export { MAP_SIZE }
